import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import { relations, sql } from 'drizzle-orm';
import {
  boolean,
  integer,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { softDelete } from '../columns/soft-delete';
import { soldiers } from './soldiers';
import { termVersions } from './term-versions';
export const termAcceptanceAcceptedByEnum = pgEnum('term_acceptance_accepted_by', [
  'participant',
  'guardian',
]);

/**
 * Prova do aceite: um registro por termo aceito em cada inscrição, com a versão exata, quando,
 * de onde e o PDF entregue à pessoa (+ hash). Nunca editado depois de criado.
 */
export const termAcceptances = pgTable(
  'term_acceptances',
  {
    id: uuid('id')
      .primaryKey()
      .$defaultFn(() => Bun.randomUUIDv7()),
    acceptedBy: termAcceptanceAcceptedByEnum('accepted_by').notNull(),
    /** Relógio do servidor — nunca vem do navegador. */
    acceptedAt: timestamp('accepted_at')
      .notNull()
      .$defaultFn(() => new Date()),
    ip: text('ip'),
    /** Porta de origem repassada pelo nginx (`X-Real-Port`); null se não houver proxy. */
    port: integer('port'),
    userAgent: text('user_agent'),
    pdfPath: text('pdf_path').notNull(),
    pdfHash: text('pdf_hash').notNull(),
    // Soldiers usam soft delete: o aceite continua como prova mesmo com a inscrição excluída.
    // `restrict` barra um delete físico feito direto no banco.
    soldierId: uuid('soldier_id')
      .references(() => soldiers.id, { onDelete: 'restrict' })
      .notNull(),
    // Versão aceita não pode sumir enquanto houver aceite apontando pra ela.
    termVersionId: uuid('term_version_id')
      .references(() => termVersions.id, { onDelete: 'restrict' })
      .notNull(),
    isActive: boolean('is_active').default(true),
    createdAt: timestamp('created_at')
      .notNull()
      .$defaultFn(() => new Date()),
    updatedAt: timestamp('updated_at')
      .$onUpdate(() => new Date())
      .notNull(),
    ...softDelete,
  },
  (table) => [
    uniqueIndex('term_acceptances_soldier_version_idx')
      .on(table.soldierId, table.termVersionId)
      .where(sql`${table.deletedAt} is null`),
  ],
);

export const termAcceptancesRelations = relations(termAcceptances, ({ one }) => ({
  soldier: one(soldiers, {
    fields: [termAcceptances.soldierId],
    references: [soldiers.id],
  }),
  termVersion: one(termVersions, {
    fields: [termAcceptances.termVersionId],
    references: [termVersions.id],
  }),
}));

export type TermAcceptance = InferSelectModel<typeof termAcceptances>;
export type CreateTermAcceptanceInput = InferInsertModel<typeof termAcceptances>;
export type UpdateTermAcceptanceInput = Partial<CreateTermAcceptanceInput>;
